import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  CircularProgress,
  useTheme
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import JobDescriptionInput from '../components/JobDescriptionInput';
import ResumeUpload from '../components/ResumeUpload';
import EmailEditor from '../components/EmailEditor';
import { useLocation, useNavigate } from 'react-router-dom';

const EmailComposePage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recruiterName, setRecruiterName] = useState('');  // Add this line
  const [userData, setUserData] = useState({ name: '', linkedinUrl: '' });




  // In EmailComposePage.js
  useEffect(() => {
    if (!user) {
      const currentUrl = window.location.href;
      navigate(`/login?redirect=${encodeURIComponent(currentUrl)}`);
      return;
    }

    const fetchUserData = async () => {
      try {
        if (user?.email) {
          const docRef = doc(db, 'users', user.email);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setUserData({
              name: docSnap.data().name || '',
              linkedinUrl: docSnap.data().linkedinUrl || ''
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Handle error gracefully
        setUserData({ name: '', linkedinUrl: '' });
      }
    };

    fetchUserData();

    // Handle extension data
    const handleMessage = (event) => {
      if (event.data.type === 'APPLICATION_DATA' && event.data.data) {
        const applicationData = event.data.data;
        if (applicationData.jobDescription) {
          // Handle both string and object formats
          setJobData(typeof applicationData.jobDescription === 'string'
            ? applicationData.jobDescription
            : applicationData.jobDescription.description || '');
        }
        setRecipientEmail(applicationData.recruiterEmail || '');
        const firstName = applicationData.profileName?.split(' ')[0] || '';
        setRecruiterName(firstName);
      }
    };

    window.addEventListener('message', handleMessage);

    const searchParams = new URLSearchParams(location.search);
    const appId = searchParams.get('appId');

    if (appId) {
      // Request data from extension
      window.postMessage({
        type: 'GET_APPLICATION_DATA',
        appId
      }, '*');
    }

    return () => window.removeEventListener('message', handleMessage);
  }, [user, location, navigate]);

  const handleJobSubmit = (data) => {
    console.log("Setting job data:", data);
    setJobData(data);
  };

  const handleResumeUpload = async (data) => {
    console.log("Resume upload received:", data.name);

    // Use a function to update state to ensure we have the latest state
    setResumeData(data);

    // Use a Promise to wait for the state update
    await new Promise(resolve => setTimeout(resolve, 200));

    if (jobData) {
      console.log("Both resume and job data available - preparing to generate email");
      console.log("Resume data:", data.name);
      console.log("Job data available:", typeof jobData === 'object' ? 'Yes (object)' : 'Yes (string)');

      // Call handleGenerateEmail with the data directly to avoid state dependency
      generateEmailWithData(data, jobData);
    }
  };

  // Add a new function that doesn't rely on state
  const generateEmailWithData = async (resumeData, jobData) => {
    if (!resumeData || !jobData) {
      console.error('Missing data for email generation');
      return;
    }

    setLoading(true);
    try {
      // Log what we're sending to help debugging
      console.log("Sending to backend:", {
        jobDescription: typeof jobData === 'string' ? 'String (length: ' + jobData.length + ')' : 'Object with description',
        resume: resumeData.content ? 'Object with content' : 'String content',
        userName: userData.name,
        linkedinUrl: userData.linkedinUrl
      });

      const response = await fetch(`${process.env.REACT_APP_BACK_END_URL}/applybridge/generate-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          jobDescription: jobData.description || jobData,
          resume: resumeData,  // Send the entire resume object with content and type
          userName: userData.name,
          linkedinUrl: userData.linkedinUrl
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      setGeneratedEmail(data.generatedEmail);
      setShowEmailEditor(true);
    } catch (error) {
      console.error('Error generating email:', error);
      alert('Failed to generate email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update your existing handleGenerateEmail to use the new function
  const handleGenerateEmail = async () => {
    if (resumeData && jobData) {
      generateEmailWithData(resumeData, jobData);
    } else {
      console.error('Missing resume or job data for email generation');
      console.log('Resume data available:', !!resumeData);
      console.log('Job data available:', !!jobData);
    }
  };

  if (!user) {
    return null; // or loading indicator
  }

  if (showEmailEditor) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', gap: 4 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
            Compose Email with AI
          </Typography>

          <TextField
            fullWidth
            label="Recipient's email address"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            sx={{ mb: 3 }}
          />

          <EmailEditor
            content={generatedEmail}
            recipientEmail={recipientEmail}
            recruiterName={recruiterName}  // Add this line
            onChange={setGeneratedEmail}
            onSend={() => {/* Handle send email */ }}
            loading={loading}
            disabled={!recipientEmail}
          />
        </Box>

        {/* Tips Section */}
        <Box sx={{ width: 300 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              background: '#fff',
              position: 'sticky',
              top: 24
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Tips for a Better Resume
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, color: theme.palette.white['900'] }}>
              {[
                "Tailor your resume to the job description",
                "Use relevant keywords from the job posting",
                "Highlight your achievements with metrics",
                "Keep it concise and well-organized"
              ].map((tip, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: theme.palette.blue['700'],
                    color: theme.palette.white['100']
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    {tip}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, display: 'flex', gap: 4 }}>
      {/* Left Column - Main Content */}
      <Box sx={{ flex: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 4 }}>
          New Application
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {jobData ? (
            // If we have job data, show only resume upload
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Upload Resume (PDF or docx)
              </Typography>
              <ResumeUpload onUpload={handleResumeUpload} />
            </Box>
          ) : (
            // If no job data, show job description input
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                Paste Job Description
              </Typography>
              <JobDescriptionInput onSubmit={setJobData} />
            </Box>
          )}

          {/* Generate Email Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={loading ?
                <ArrowForwardIcon /> :
                <ArrowForwardIcon />
              }
              disabled={!jobData || !resumeData || loading}
              onClick={handleGenerateEmail}
              sx={{
                borderRadius: '24px',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                textTransform: 'none',
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              }}
            >
              {loading ? 'Generating...' : 'Generate Email'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Right Column - Tips */}
      <Box sx={{ width: 300 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            background: '#fff',
            position: 'sticky',
            top: 24
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Tips for a Better Resume
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, color: theme.palette.white['900'] }}>
            {[
              "Tailor your resume to the job description",
              "Use relevant keywords from the job posting",
              "Highlight your achievements with metrics",
              "Keep it concise and well-organized"
            ].map((tip, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.blue['700'],
                  color: 'white'
                }}
              >
                <Typography variant="body2" sx={{ color: theme.palette.white['100'] }}>
                  {tip}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailComposePage;